import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import {
  ComposableMap as _ComposableMap,
  Geographies,
  Geography as _Geography
} from 'react-simple-maps';
import { sizes } from 'style';
import MapTooltip from '../MapTooltip';

export const MapContainer = styled.div`
  position: relative;
  width: 100vw;
  margin-top: 24px;
  margin-left: -20px;

  @media ${sizes.tabletPortrait} {
    width: 100%;
    max-width: 1400px;
    display: flex;
    justify-content: center;
    margin-top: 40px;
    margin-left: 0;
    margin-bottom: 80px;
  }
`;

const Geography = styled(_Geography)`
  transition: fill 200ms;
  pointer-events: none;

  @media ${sizes.tabletPortrait} {
    pointer-events: all;
  }
`;

const ComposableMap = styled(_ComposableMap)`
  width: 100%;
  height: 100%;
`;

const GEO_URL_WORLD = `${process.env.PUBLIC_URL}/worldmap.json`;

const Map = ({ onCountrySelect, team }) => {
  const [memberCountPerCountry, setMemberCountPerCountry] = useState([]);
  const [maxMembers, setMaxMembers] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    if (team) {
      setMemberCountPerCountry(
        team.reduce((acc, currentTeamMember) => {
          const existingEntryIndex = acc.findIndex(
            (item) => item.country === currentTeamMember.country
          );
          if (existingEntryIndex > -1) {
            acc[existingEntryIndex].memberCount += 1;
          } else {
            acc.push({ country: currentTeamMember.country, memberCount: 1 });
          }
          return acc;
        }, [])
      );
    }
  }, [team]);

  useEffect(() => {
    if (memberCountPerCountry?.length) {
      setMaxMembers(
        Math.max(...memberCountPerCountry.map((country) => country.memberCount))
      );
    }
  }, [memberCountPerCountry]);

  const colorScale = scaleLinear().domain([1, maxMembers]).range(['#077B7C', '#B2F8F8']);

  return (
    <MapContainer>
      <ComposableMap
        width={730}
        height={370}
        projectionConfig={{
          scale: 155,
          center: [22, 8]
        }}
      >
        <Geographies geography={GEO_URL_WORLD}>
          {({ geographies }) =>
            geographies
              .filter((d) => d.properties.REGION_UN !== 'Antarctica')
              .map((geo) => {
                let countryName;
                switch (geo.properties.name) {
                  case 'Bosnia and Herz.':
                    countryName = 'Bosnia and Herzegovina';
                    break;
                  case 'Czechia':
                    countryName = 'Czech Republic';
                    break;
                  case 'United States of America':
                    countryName = 'United States';
                    break;
                  default:
                    countryName = geo.properties.name;
                }

                const numberOfMembers = memberCountPerCountry.find((item) => {
                  const splitCountry = item.country.split('(');
                  const country = splitCountry?.[0].trim();
                  return country === countryName;
                })?.memberCount;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={numberOfMembers > 0 ? colorScale(numberOfMembers) : '#ffffff10'}
                    stroke={theme.colors.black}
                    strokeWidth={0.5}
                    style={{
                      default: {
                        outline: 'none',
                        opacity: 0.7
                      },
                      hover: {
                        outline: 'none',
                        opacity: numberOfMembers > 0 ? 1 : 0.7,
                        cursor: numberOfMembers > 0 ? 'pointer' : 'default'
                      },
                      pressed: {
                        outline: 'none'
                      }
                    }}
                    onClick={() => {
                      if (!numberOfMembers) return;
                      onCountrySelect(countryName);
                    }}
                  />
                );
              })
          }
        </Geographies>
      </ComposableMap>
      <MapTooltip />
    </MapContainer>
  );
};

Map.propTypes = {
  team: PropTypes.array.isRequired,
  onCountrySelect: PropTypes.func.isRequired
};

export default Map;
